"use client";

import { Text } from "@afa-shared/afa-components";
import Heading from "@afa-shared/afa-components/dist/Heading";
import Icon from "@afa-shared/afa-components/dist/Icon";
import clsx from "clsx";
import { useId, useState } from "react";

import { CookieConsentCategory, useCookieConsent } from "hooks/useCookieConsent";

import feedbackStyles from "./FeedbackBlock.module.css";
import { trackVoteClick } from "./feedbackTracker";

declare global {
  interface Window {
    /** Merkle survey command queue, e.g. `window._ocs.push("show")`. */
    _ocs?: unknown[];
  }
}

interface IFeedbackBlockProps {
  /** Section title, e.g. "Lämna feedback". Sent (normalised) as the `title` event param. */
  title?: string;
  /** Intro paragraph shown under the heading. */
  description?: string;
  /** 1-based section position, derived like other tracking contracts. */
  index?: number;
  /** Insurance the page is tied to (display name); mapped to the GA analytics value. */
  insuranceName?: string;
}

const DEFAULT_TITLE = "Lämna feedback";
const DEFAULT_DESCRIPTION =
  "Ge oss din feedback genom att klicka på en tumme upp eller ner. Du får sedan svara på 3 korta frågor som hjälper oss att förbättra innehållet.";

/**
 * Opens the Merkle survey modal. The survey command queue (`window._ocs`) is
 * provided by the Merkle survey script, which is loaded via GTM. If it is
 * missing (e.g. the script was blocked or failed to load) we warn instead of
 * crashing so a failed vote never breaks the page.
 */
const showMerkleSurvey = (): void => {
  if (typeof window._ocs?.push !== "function") {
    console.warn(
      "FeedbackBlock: Merkle survey unavailable (window._ocs missing); could not open the survey.",
    );
    return;
  }

  // Always reset before show so the survey reopens in its initial state.
  window._ocs.push("reset");
  window._ocs.push("show");
};

/**
 * CES feedback module shown at the bottom of eligible content pages.
 *
 * - Only rendered when the user has accepted analytics (statistical) cookies.
 * - On click pushes a `vote_click` event to the dataLayer (analytics) and opens
 *   the Merkle survey modal via `_ocs.push("reset")` + `_ocs.push("show")`. The
 *   survey script itself is loaded via GTM.
 * - After a vote the element hides itself and only reappears on page reload,
 *   matching the Hotjar button behaviour.
 */
export default function FeedbackBlock({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  index,
  insuranceName,
}: IFeedbackBlockProps) {
  const analyticsConsentGiven = useCookieConsent(CookieConsentCategory.statistical);
  const [hasVoted, setHasVoted] = useState(false);
  const headingId = useId();

  if (!analyticsConsentGiven || hasVoted) {
    return null;
  }

  const handleVote = (linkText: "Positiv" | "Negativ") => {
    trackVoteClick({ title, index, link_text: linkText, insurance: insuranceName });
    showMerkleSurvey();
    setHasVoted(true);
  };

  return (
    <section
      className={feedbackStyles.feedback}
      aria-labelledby={headingId}
      data-blockname="feedback"
    >
      <div className={feedbackStyles.text}>
        <Heading variant="h3" id={headingId} removeMargin className={feedbackStyles.heading}>
          {title}
        </Heading>
        <Text variant={"paragraph"} removeMargin>
          {description}
        </Text>
      </div>
      <div className={feedbackStyles.actions}>
        <button
          type="button"
          className={feedbackStyles.voteButton}
          onClick={() => handleVote("Positiv")}
          aria-label="Tumme upp"
        >
          <Icon name="thumb_up" size="sm" className={feedbackStyles.voteIcon} />
        </button>
        <button
          type="button"
          className={clsx(feedbackStyles.voteButton, feedbackStyles.voteButtonNegative)}
          onClick={() => handleVote("Negativ")}
          aria-label="Tumme ned"
        >
          <Icon name="thumb_up" size="sm" className={feedbackStyles.voteIcon} />
        </button>
      </div>
    </section>
  );
}
